import {
  BarChartOutlined,
  BarcodeOutlined,
  BookOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  FilePptOutlined,
  IdcardOutlined,
  LogoutOutlined,
  NotificationOutlined,
  PartitionOutlined,
  ReconciliationOutlined,
  RiseOutlined,
  ScheduleOutlined,
  SnippetsOutlined,
  UserOutlined,
  WechatOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import { useAuthentication } from '@hooks/authentication';
import { usePermissions } from '@hooks/permissions';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { filterMenuItemsByPermissions } from '@consts/permissions';
import { Layout, Menu, MenuProps } from 'antd';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import 'overlayscrollbars/overlayscrollbars.css';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Sider.scss';

interface SiderProps {
  hide?: boolean;
}

const DEFAULT_OPEN_KEYS = [
  'sub1',
  'sub2',
  'sub3',
  'sub4',
  'sub5',
  'notices-menu',
  'report-menu',
  'schedule-menu',
  'workshops-menu',
  'users',
  'memberships',
];

type MenuItem = Required<MenuProps>['items'][number];

const MENU_ITEMS: MenuItem[] = [
  {
    label: <Link to={'/dashboard'}>대시보드</Link>,
    icon: <BarChartOutlined />,
    key: 'dashboard',
  },
  {
    label: <Link to={'/report/products'}>보고서</Link>,
    icon: <ReconciliationOutlined />,
    key: 'reports',
    children: [
      {
        label: <Link to={'/report/products'}>상품 전환</Link>,
        key: 'product-report',
      },
      {
        label: <Link to={'/report/product-exposure'}>상품 노출</Link>,
        key: 'product-exposure-report',
      },
      {
        label: <Link to={'/report/sales'}>파트너 판매</Link>,
        key: 'sales-report',
      },
      {
        label: <Link to={'/report/purchases'}>유저 구매</Link>,
        key: 'purchases-report',
      },
      {
        label: <Link to={'/report/net-sales-report'}>이익관리</Link>,
        key: 'net-sales-report',
      },
    ],
  },

  {
    label: '유저관리',
    icon: <UserOutlined />,
    key: 'users',
    children: [
      {
        label: <Link to={'/users'}>유저 관리</Link>,
        key: 'users-management',
      },
      {
        label: <Link to={'/partner-users'}>파트너 관리</Link>,
        key: 'partner-users',
      },
      {
        label: <Link to={'/admin-users'}>어드민 유저 관리</Link>,
        key: 'admin-users',
      },
    ],
  },

  {
    label: <Link to={'/user-contents'}>고객 참여 관리</Link>,
    icon: <RiseOutlined />,
    key: 'customer-engagement',
    children: [
      {
        label: <Link to={'/user-contents'}>라운지 콘텐츠</Link>,
        key: 'user-contents',
      },
      {
        label: <Link to={'/affiliates'}>Affiliate 링크 관리</Link>,
        key: 'affiliates',
      },
      {
        label: <Link to={'/comment-monitoring'}>댓글 모니터링</Link>,
        key: 'comment-monitoring',
      },
      {
        label: <Link to={'/review-events'}>리뷰 이벤트 관리</Link>,
        key: 'review-events',
      },
      {
        label: <Link to={'/customer-request'}>클래스 오픈 요청</Link>,
        key: 'customer-request',
      },
      {
        label: <Link to={'/user-event-records'}>이벤트 참여</Link>,
        key: 'user-event-records',
      },
    ],
  },
  {
    label: <Link to={'/products'}>상품 관리</Link>,
    icon: <BookOutlined />,
    key: 'products',
  },
  {
    label: <Link to={'/products-inventory'}>상품 재고 관리</Link>,
    icon: <BarcodeOutlined />,
    key: 'products-inventory',
    children: [
      {
        label: <Link to={'/products-inventory'}>상품 재고 마감</Link>,
        key: 'products-inventory-list',
      },
      {
        label: <Link to={'/product-inventory-closing-history'}>재고 마감 이력</Link>,
        key: 'product-inventory-closing-history',
      },
    ],
  },
  {
    label: '수강증 관리',
    icon: <SnippetsOutlined />,
    key: 'invoices-management',
    children: [
      {
        label: <Link to={'/class-invoices'}>클래스 수강증</Link>,
        key: 'class-invoices',
      },
      {
        label: <Link to={'/program-invoices'}>(new) 수강증</Link>,
        key: 'program-invoices',
      },
    ],
  },
  {
    label: <Link to={'/program-schedules'}>프로그램 일정 관리</Link>,
    icon: <CalendarOutlined />,
    key: 'program-schedules-management',
    children: [
      {
        label: <Link to={'/program-schedules'}>프로그램 일정 관리</Link>,
        key: 'program-schedules',
      },
      {
        label: <Link to={'/program-schedule-groups'}>프로그램 그룹 관리</Link>,
        key: 'program-schedule-groups',
      },
      {
        label: <Link to={'/program-reports'}>리포트 관리</Link>,
        key: 'program-reports',
      },
    ],
  },
  {
    label: '예약 성사',
    icon: <CheckCircleOutlined />,
    key: 'reservation-success-menu',
    children: [
      {
        label: <Link to={'/reservation-success'}>대시보드</Link>,
        key: 'reservation-success-dashboard',
      },
      {
        label: <Link to={'/group-monitoring'}>그룹 현황</Link>,
        key: 'group-monitoring',
      },
      {
        label: <Link to={'/grouping-agent-runs'}>그룹핑 실행</Link>,
        key: 'grouping-agent-runs',
      },
      {
        label: <Link to={'/reservation-success-product-indicators'}>상품 보조지표</Link>,
        key: 'reservation-success-product-indicators',
      },
      {
        label: <Link to={'/reservation-success-alerts'}>파트너 알림</Link>,
        key: 'reservation-success-alerts',
      },
      {
        label: <Link to={'/reservation-success-batch-runs'}>배치 실행</Link>,
        key: 'reservation-success-batch-runs',
      },
      {
        label: <Link to={'/reservation-success-metrics'}>성과 지표</Link>,
        key: 'reservation-success-metrics',
      },
    ],
  },
  {
    label: <Link to={'/product-orders'}>결제 관리</Link>,
    icon: <DollarOutlined />,
    key: 'payment-statements-management',
    children: [
      {
        label: <Link to={'/product-orders'}>통합결제내역</Link>,
        key: 'product-orders',
      },
      {
        label: <Link to={'/payment-statements'}>결제내역</Link>,
        key: 'payment-statements',
      },
      {
        label: <Link to={'/payment-templates'}>발행된 결제 템플릿</Link>,
        key: 'payment-templates',
      },
      {
        label: <Link to={'/settlements'}>정산내역</Link>,
        key: 'settlements',
      },
      {
        label: <Link to={'/commission-rate-template'}>수수료율 템플릿</Link>,
        key: 'commission-rate-template',
      },
      {
        label: <Link to={'/product-orders-refund-schedule'}>환불 스케줄 내역</Link>,
        key: 'product-orders-refund-schedule',
      },
    ],
  },
  {
    label: '멤버십',
    icon: <IdcardOutlined />,
    key: 'memberships',
    children: [
      {
        label: <Link to={'/memberships'}>멤버십 관리</Link>,
        key: 'memberships-list',
      },
      {
        label: <Link to={'/membership-users'}>멤버십 가입 유저</Link>,
        key: 'membership-users',
      },
    ],
  },
  {
    label: <Link to={'/naver-orders'}>외부 판매 채널</Link>,
    icon: <ScheduleOutlined />,
    key: 'sales-channels',
    children: [
      // {
      //   label: <Link to={'/sales-channels/products'}>외부 채널 상품 관리</Link>,
      //   key: 'sales-channel-products',
      // },
      // {
      //   label: <Link to={'/naver-orders'}>네이버 판매 내역</Link>,
      //   key: 'naver-orders',
      // },
      // {
      //   label: <Link to={'/kakao-orders'}>카카오(R) 판매내역</Link>,
      //   key: 'kakao-orders',
      // },
      // {
      //   label: <Link to={'/inbound-orders'}>인바운드 판매 내역</Link>,
      //   key: 'inbound-orders',
      // },
      {
        label: <Link to={'/naver-commerce-orders'}>네이버 주문 연동(API)</Link>,
        key: 'naver-commerce-orders',
      },
      {
        label: <Link to={'/naver-commerce-products'}>네이버 상품(API)</Link>,
        key: 'naver-commerce-products',
      },
    ],
  },
  {
    label: <Link to={'/push-notifications'}>푸시 & 카카오 알림</Link>,
    icon: <NotificationOutlined />,
    key: 'notifications',
    children: [
      {
        label: <Link to={'/push-notifications'}>푸시 알림</Link>,
        key: 'push-notifications',
      },
      {
        label: <Link to={'/kakao-notifications'}>카카오 알림톡</Link>,
        key: 'kakao-notifications',
      },
      {
        label: <Link to={'/kakao-reserved-notifications'}>카카오 알림톡(예약)</Link>,
        key: 'kakao-reserved-notifications',
      },
    ],
  },
  {
    label: <Link to={'/main-feed'}>상품 큐레이션</Link>,
    icon: <ScheduleOutlined />,
    key: 'curation',
    children: [
      {
        label: <Link to={'/main-feed-v2'}>메인피드 관리(new)</Link>,
        key: 'main-feed-v2',
      },
      {
        label: '특가탭 관리',
        key: 'deals-group',
        children: [
          {
            label: <Link to={'/deals'}>특가홈 관리</Link>,
            key: 'deals',
          },
          {
            label: <Link to={'/time-sales'}>타임세일 관리</Link>,
            key: 'time-sales',
          },
          {
            label: <Link to={'/last-chance-monitoring'}>라스트찬스 모니터링</Link>,
            key: 'last-chance-monitoring',
          },
        ],
      },
      {
        label: <Link to={'/product-collections'}>기획전 관리</Link>,
        key: 'product-collections',
      },
      {
        label: <Link to={'/banners'}>배너 관리</Link>,
        key: 'banners',
      },
      {
        label: <Link to={'/feed-elements'}>피드 표시 항목 관리</Link>,
        key: 'feed-elements',
      },
      {
        label: <Link to={'/customer-benefit-events'}>이벤트 모아보기</Link>,
        key: 'customer-benefit-events',
      },
      {
        label: <Link to={'/campaigns'}>캠페인 관리</Link>,
        key: 'campaigns',
      },
      {
        label: <Link to={'/main-feed'}>메인피드 관리(구)</Link>,
        key: 'main-feed',
      },
    ],
  },
  {
    label: <Link to={'/chat-rooms'}>채팅 관리</Link>,
    icon: <WechatOutlined />,
    key: 'chat-monitor',
    children: [
      {
        label: <Link to={'/chat-rooms'}>대화방 모니터링</Link>,
        key: 'chat-rooms',
      },
      {
        label: <Link to={'/chat-messages'}>메시지 모니터링</Link>,
        key: 'chat-messages',
      },
      {
        label: <Link to={'/violation-detected-messages'}>정책 위반 의심 메시지</Link>,
        key: 'violation-detected-messages',
      },
      {
        label: <Link to={'/chat-rooms-v2'}>(new) 대화방 모니터링</Link>,
        key: 'chat-rooms-v2',
      },
    ],
  },
  {
    label: <Link to={'/point-template'}>포인트/쿠폰/이벤트</Link>,
    icon: <FilePptOutlined />,
    key: 'discount-coupon',
    children: [
      {
        label: <Link to={'/discount-coupon'}>할인 쿠폰 관리</Link>,
        key: 'discount-coupon',
      },
      {
        label: <Link to={'/user-discount-coupon'}>할인 쿠폰 발행내역</Link>,
        key: 'user-discount-coupon',
      },
      {
        label: <Link to={'/point-template'}>포인트 템플릿</Link>,
        key: 'point-template',
      },
      {
        label: <Link to={'/coupon'}>포인트 쿠폰 관리</Link>,
        key: 'coupon',
      },
      {
        label: <Link to={'/coupon-log'}>포인트 쿠폰 사용내역</Link>,
        key: 'coupon-log',
      },
      {
        label: <Link to={'/point-log'}>포인트 적립/사용 내역</Link>,
        key: 'point-log',
      },
      {
        label: <Link to={'/join-promotion'}>회원가입 이벤트</Link>,
        key: 'join-promotion',
      },
      {
        label: <Link to={'/join-promotion-log'}>회원가입 이벤트 이력</Link>,
        key: 'join-promotion-log',
      },
      {
        label: <Link to={'/events'}>이벤트 관리</Link>,
        key: 'event-management',
      },
    ],
  },
  {
    label: <Link to={'/benepia/users'}>베네피아</Link>,
    icon: <RocketOutlined />,
    key: 'benepia',
    children: [
      {
        label: <Link to={'/benepia/users'}>유저 관리</Link>,
        key: 'benepia-users',
      },
      {
        label: <Link to={'/benepia/banners'}>배너 관리</Link>,
        key: 'benepia-banners',
      },
      // {
      //   label: <Link to={'/benepia/payments'}>결제 내역</Link>,
      //   key: 'benepia-payments',
      // },
      // {
      //   label: <Link to={'/benepia/products'}>상품 관리</Link>,
      //   key: 'benepia-products',
      // },
      // {
      //   label: <Link to={'/benepia/curation'}>큐레이션</Link>,
      //   key: 'benepia-curation',
      // },
    ],
  },
  {
    label: <Link to={'/notices'}>시스템 관리</Link>,
    icon: <PartitionOutlined />,
    key: 'system-management',
    children: [
      {
        label: <Link to={'/notices'}>공지사항</Link>,
        key: 'notices',
      },
      {
        label: <Link to={'/remote-config'}>플랫폼 관리</Link>,
        key: 'remote-config',
      },
      {
        label: <Link to={'/sqs-message-management'}>SQS 메시지 관리</Link>,
        key: 'sqs-message-management',
      },
      {
        label: <Link to={'/naver-commerce-monitoring'}>네이버 커머스 모니터링</Link>,
        key: 'naver-commerce-monitoring',
      },
      {
        label: <Link to={'/payments'}>결제 내역 관리</Link>,
        key: 'payments',
      },
      {
        label: <Link to={'/product-clustering'}>상품 클러스터링 관리</Link>,
        key: 'product-clustering',
      },
      {
        label: <Link to={'/utility'}>유틸리티</Link>,
        key: 'utility',
      },
      {
        label: <Link to={'/integration-apis'}>연동 API</Link>,
        key: 'integration-apis',
      },
    ],
  },

  { key: 'bottom-divider' },
  { label: '로그아웃', icon: <LogoutOutlined />, key: 'logout' },
];

const Sider: React.FC<SiderProps> = (props) => {
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const dialog = useOkCancelDialog();
  const auth = useAuthentication();
  const { userPermissions } = usePermissions();

  // 권한에 따라 메뉴 필터링
  const filteredMenuItems = filterMenuItemsByPermissions(MENU_ITEMS, userPermissions);

  if (props.hide) {
    return <div id="hidden-sider"></div>;
  }

  return (
    <>
      <div>
        <button
          className="link-button"
          style={{ width: '48px', height: '48px', position: 'fixed', top: 0, left: 0, zIndex: 1025 }}
          onClick={() => {
            setShowMenu((value) => !value);
          }}></button>
      </div>
      <Layout.Sider width={200} className={showMenu ? 'sider show' : 'sider'}>
        <OverlayScrollbarsComponent
          defer
          options={{
            scrollbars: { autoHide: 'scroll' },
            overflow: {
              x: 'hidden',
            },
          }}
          style={{
            marginTop: 0,
            position: 'relative',
            maxHeight: '100vh',
            overflow: 'auto',
            width: '100%',
          }}>
          <div className="menu-title">아이고고 어드민</div>
          <div
            style={{
              color: 'white',
              textAlign: 'center',
              wordBreak: 'break-word',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              paddingBottom: '16px',
            }}>
            {auth.authStore.user?.email}
          </div>
          <Menu
            mode="inline"
            theme="dark"
            defaultOpenKeys={DEFAULT_OPEN_KEYS}
            items={filteredMenuItems}
            style={{ borderRight: 0 }}
            onClick={(info) => {
              if (info.key === 'logout') {
                dialog.open({
                  title: '로그아웃',
                  content: '로그아웃 할까요?',
                  type: 'ok_cancel',
                  onConfirm: () => {
                    auth.logout();
                  },
                });
              }
            }}></Menu>
        </OverlayScrollbarsComponent>
      </Layout.Sider>
    </>
  );
};

export default Sider;
